import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { PlayArrow, Schedule } from '@mui/icons-material';
import * as adminApi from '@/api/admin';
import type { AdminWorker } from '@/api/admin';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDateTime } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/admin/workers/')({
  component: AdminWorkersPage,
});

const LABELS: Record<string, { title: string; desc: string }> = {
  'suspension-checker': {
    title: 'Suspensión por mora',
    desc: 'Suspende tenants en PastDue que superaron el período de gracia.',
  },
  'certificate-expiry': {
    title: 'Vencimiento de certificados',
    desc: 'Escanea certificados ARCA por vencer y emite alertas.',
  },
};

function fmtInterval(seconds: number): string {
  if (seconds <= 0) return '—';
  if (seconds % 3600 === 0) return `cada ${seconds / 3600} h`;
  if (seconds % 60 === 0) return `cada ${seconds / 60} min`;
  return `cada ${seconds}s`;
}

function AdminWorkersPage(): JSX.Element {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['admin-workers'],
    queryFn: adminApi.listWorkers,
    refetchInterval: 30_000,
  });

  const runMut = useMutation({
    mutationFn: (name: string) => adminApi.runWorker(name),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-workers'] }),
    onError: (e) => setError(toApiError(e).message),
  });

  if (q.isLoading) return <LoadingScreen message="Cargando workers…" />;

  if (q.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{toApiError(q.error).message}</Alert>
      </Box>
    );
  }

  const workers = q.data ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Stack sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Schedule color="primary" /> Workers
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Estado de los jobs de fondo. El estado es in-memory (se resetea al reiniciar el proceso).
        </Typography>
      </Stack>

      {error ? (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {workers.length === 0 ? (
        <Alert severity="info">No hay workers registrados (el proceso recién arrancó o están todos apagados).</Alert>
      ) : (
        <Grid container spacing={2}>
          {workers.map((w) => (
            <Grid key={w.name} size={{ xs: 12, md: 6 }}>
              <WorkerCard
                worker={w}
                running={runMut.isPending && runMut.variables === w.name}
                onRun={() => runMut.mutate(w.name)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

function WorkerCard({
  worker: w,
  running,
  onRun,
}: {
  worker: AdminWorker;
  running: boolean;
  onRun: () => void;
}): JSX.Element {
  const meta = LABELS[w.name] ?? { title: w.name, desc: '' };
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            {meta.title}
          </Typography>
          <Chip
            size="small"
            color={w.enabled ? 'success' : 'default'}
            variant={w.enabled ? 'filled' : 'outlined'}
            label={w.enabled ? 'Activo' : 'Apagado'}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {meta.desc}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={0.75}>
          <Row label="Frecuencia" value={fmtInterval(w.intervalSeconds)} />
          <Row
            label="Última corrida"
            value={
              w.lastRunAt
                ? `${formatDateTime(w.lastRunAt)}${w.lastDurationMs != null ? ` · ${Math.round(w.lastDurationMs)} ms` : ''}`
                : 'nunca'
            }
          />
          <Row label="Próxima (estimada)" value={w.nextRunAt ? formatDateTime(w.nextRunAt) : '—'} />
          <Row label="Corridas" value={String(w.runCount)} />
          <Row
            label="Resultado"
            value={
              w.lastError ? (
                <Typography variant="caption" color="error" sx={{ fontFamily: 'var(--font-mono)' }}>
                  {w.lastError}
                </Typography>
              ) : w.lastResult ? (
                <Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)' }}>
                  {w.lastResult}
                </Typography>
              ) : (
                '—'
              )
            }
          />
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Button size="small" variant="outlined" startIcon={<PlayArrow />} disabled={running} onClick={onRun}>
            {running ? <CircularProgress size={18} /> : 'Ejecutar ahora'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right' }}>
        {typeof value === 'string' ? <Typography variant="body2">{value}</Typography> : value}
      </Box>
    </Box>
  );
}
